// The Overwolf change-plan page (/ow-plan).
//
// Opened from the Songhunt menu inside the Overwolf app, e.g.
//   /songhunt/ow-plan?d=<SH1 blob>&utm_source=overwolf&utm_campaign=ow_change_plan
//
// The `d` blob carries the signed-in user's e-mail, password and current plan
// (see api/account-link.ts, and the security note in it), so the page logs them
// in silently and never asks a user who is already signed in to sign in again.
//
// Shares its constraints with OwSurvey and OwCancel:
//
// - It renders OUTSIDE the App shell (a top-level route in app/router.tsx), so
//   there is no NavBar, no store bootstrap and no session to inherit. Changing a
//   plan never leaves this page: there is no endpoint behind it yet, so the
//   change is applied to the card in place and the team is notified out of band.
// - Because AppLoader is not mounted, this route removes the static
//   #app-preloader itself (see the react-basename-bare-path-preloader-hang note).
// - The affiliate-link popup must never appear at someone managing their
//   subscription. The link always carries utm_source=overwolf, and setting the
//   UTM tags below latches isOverwolfSource(), which hard-disables the timer for
//   the rest of the tab session even though login() would otherwise start it.
//
// LOGGING RULE (from the account-link spec): log THAT a link was opened and what
// happened, never the URL, the blob, or any decoded field. These logs get pasted
// into support tickets.

import { useCallback, useEffect, useMemo, useState } from 'react';
import SonghuntLogo from '../../shared/components/SonghuntLogo';
import { assetUrl } from '../../shared/asset-url';
import { logger } from '../../shared/logging/logger';
import { createCheckoutSession, login as apiLogin } from '../../api/http-req';
import { decodeAccountPayload, isFresh, type AccountPlan } from '../../api/account-link';
import AccountRequestEmailStep from '../../shared/support/AccountRequestEmail';
import { buildPlanChangeEmail } from '../../shared/support/account-request-email';
import { useUserStore } from '../../stores/user-store';
import type { User } from '../../interfaces/user';
import './OwPlan.scss';

type Status =
  | 'checking'     // decoding + signing in
  | 'ready'        // we know who they are and what they are on
  | 'checkout'     // handing off to Stripe for a paid plan
  | 'unavailable'; // link missing, malformed, stale, or the sign-in was rejected

interface PlanRow {
  key: AccountPlan;
  name: string;
  /** One line of who the plan is for, shown in the card's banner. */
  sub: string;
  /** Confirmed 2026-08-22. Must match the signup landing's Overwolf prices; the
   *  change-plan page deliberately carries NO first-month discount - that promo
   *  belongs to signup only. */
  price: string;
  features: string[];
  /** What is lost by leaving this plan - shown in the downgrade confirmation. */
  loses: string[];
}

// Ordered cheapest to richest: the row above yours is a downgrade, the row below
// is an upgrade, and the page derives the direction from this order alone.
const PLAN_ROWS: PlanRow[] = [
  {
    key: 'none',
    name: 'No plan',
    sub: 'Just trying it out',
    price: '$0',
    features: ['30 minutes of Adaptive Music per session', 'Unlimited Prompt Song Searches'],
    loses: [],
  },
  {
    key: 'hunter',
    name: 'Personal Use',
    sub: 'For playing on your own',
    price: '$5',
    features: [
      'Unlimited Adaptive Music',
      'Unlimited Prompt and Filter Song Searches',
      'Explorer Songhunt.ai access',
    ],
    loses: ['Unlimited Adaptive Music - back to 30 minutes a session', 'Explorer Songhunt.ai access'],
  },
  {
    key: 'streamer',
    name: 'Streamer',
    sub: 'For streaming and creating',
    price: '$9.99',
    features: [
      'Everything in Personal Use',
      "'Stream Mode' - 100% licensed music you can stream",
      'Unlimited Rematches',
      'Hunter Songhunt.ai access',
    ],
    loses: [
      "'Stream Mode' - 100% licensed music you can stream",
      'Unlimited Rematches',
      'Hunter Songhunt.ai access',
    ],
  },
];

const rank = (plan: AccountPlan) => PLAN_ROWS.findIndex(row => row.key === plan);

/** Keeps the affiliate timer off for this tab (see the header note). */
function latchOverwolfUtm(params: URLSearchParams): void {
  const utm = new URLSearchParams();
  for (const [key, value] of params) {
    if (key.startsWith('utm_')) utm.set(key, value);
  }
  if ([...utm.keys()].length > 0) useUserStore.getState().setUtmTags(utm.toString());
}

export default function OwPlan() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);

  const [status, setStatus] = useState<Status>('checking');
  const [currentPlan, setCurrentPlan] = useState<AccountPlan>('none');
  const [email, setEmail] = useState('');
  const [user, setUser] = useState<User | null>(null);
  // One dialog, two steps. A downgrade opens at 'confirm' and moves to 'email'
  // when the user goes through with it; a free upgrade has nothing to warn about,
  // so it opens straight at 'email'. Both end in the same place, which is why the
  // e-mail step lives in the dialog rather than on the page behind it.
  const [dialogPlan, setDialogPlan] = useState<PlanRow | null>(null);
  const [dialogStep, setDialogStep] = useState<'confirm' | 'email'>('confirm');
  /** True when the checkout redirect has not happened in a reasonable time. */
  const [checkoutStalled, setCheckoutStalled] = useState(false);

  useEffect(() => {
    document.getElementById('app-preloader')?.remove();
    document.title = 'Songhunt - Your plan';
  }, []);

  // Decode, validate, sign in. Runs once: the payload is a property of the URL.
  useEffect(() => {
    latchOverwolfUtm(params);

    const payload = decodeAccountPayload(params.get('d') ?? '');
    if (!payload) {
      logger.warn('app', 'ow-plan link rejected', { reason: 'malformed' });
      setStatus('unavailable');
      return;
    }
    if (!isFresh(payload)) {
      // Also the only defence against a front-truncated blob, whose SH1 marker
      // survives intact - see account-link.ts.
      logger.warn('app', 'ow-plan link rejected', { reason: 'expired' });
      setStatus('unavailable');
      return;
    }

    setCurrentPlan(payload.plan);
    setEmail(payload.email);

    let cancelled = false;
    // Two-argument then(), NOT .then().catch(): only a REJECTED sign-in may put
    // the page into the failure state. With a trailing .catch(), anything that
    // threw inside the success handler - a store side effect, an analytics
    // script - would tell a user who just signed in fine that their link had
    // expired, and send them back to Overwolf to mint another one.
    apiLogin(payload.email, payload.password).then(
      (body: User) => {
        if (cancelled) return;

        try {
          useUserStore.getState().setUser(body);
        } catch (err) {
          // setUser injects analytics and touches several stores. None of that
          // is worth the page: they are signed in either way.
          logger.warn('app', 'ow-plan post-sign-in setup failed', { err: String(err) });
        }

        setUser(body);
        // Trust the live account over the link for the one plan we can verify:
        // a user who subscribed since the link was minted is on Streamer now.
        if (body?.isSubscribed) setCurrentPlan('streamer');
        logger.info('app', 'ow-plan opened', { plan: payload.plan });
        setStatus('ready');
      },
      () => {
        if (cancelled) return;
        logger.warn('app', 'ow-plan link rejected', { reason: 'sign-in failed' });
        setStatus('unavailable');
      },
    );

    return () => {
      cancelled = true;
    };
  }, [params]);

  // A successful checkout replaces the page, so this timer only ever fires when
  // the hand-off failed.
  useEffect(() => {
    if (status !== 'checkout') return;
    setCheckoutStalled(false);
    const stall = setTimeout(() => setCheckoutStalled(true), 8000);
    return () => clearTimeout(stall);
  }, [status]);

  // Moving UP is a purchase - both Personal Use ($5) and Streamer ($9.99) - so it
  // runs the real thing: the same Stripe checkout session normal signup uses. The
  // user is already signed in by the time they get here, which is exactly the
  // branch LoginRegister takes for a logged-in user.
  //
  // This is also what stops a second free month. The free month is a SIGNUP promo
  // that ends by dropping the account to No plan; coming back through this page
  // pays, because this path always goes to checkout and never grants a plan.
  //
  // TODO: createCheckoutSession() posts a hardcoded productId 5 - the web Hunter
  // product - so until the backend supplies the two Overwolf product ids, both
  // buttons buy that. Personal Use lands on the right price by coincidence;
  // Streamer undercharges. Pass the id per plan once they exist.
  const startCheckout = useCallback(
    (row: PlanRow) => {
      logger.info('app', 'ow-plan checkout started', { from: currentPlan, to: row.key });
      setStatus('checkout');
      // Redirects the page; nothing after this runs on the happy path.
      createCheckoutSession(null);
    },
    [currentPlan],
  );

  // Going DOWN has no endpoint and no payment to take, so the request leaves by
  // e-mail. The page shows what to send rather than pretending the plan moved.
  //
  // TODO: when a downgrade endpoint lands, call it here and drop the e-mail step.
  const applyChange = useCallback(
    (row: PlanRow) => {
      if (rank(row.key) > rank(currentPlan)) {
        setDialogPlan(null);
        startCheckout(row);
        return;
      }

      logger.info('app', 'ow-plan change requested', { from: currentPlan, to: row.key });
      setDialogPlan(row);
      setDialogStep('email');
    },
    [currentPlan, startCheckout],
  );

  const confirmDowngrade = useCallback(() => {
    if (dialogPlan) applyChange(dialogPlan);
  }, [applyChange, dialogPlan]);

  const closeDialog = useCallback(() => {
    setDialogPlan(null);
    setDialogStep('confirm');
  }, []);

  const currentRow = PLAN_ROWS.find(row => row.key === currentPlan) ?? PLAN_ROWS[0];

  return (
    <div className="ow-plan">
      <main className="ow-plan__card">
        <div className="ow-plan__lockup">
          <SonghuntLogo className="ow-plan__sh-logo" />
          <span className="ow-plan__plus" aria-hidden="true">
            +
          </span>
          <img
            className="ow-plan__ow-logo"
            src={assetUrl('assets/logos/overwolf-logo.svg')}
            alt="Overwolf"
          />
        </div>

        {status === 'checking' && (
          <div className="ow-plan__result" role="status">
            <span className="ow-plan__spinner" aria-hidden="true" />
            <h1 className="ow-plan__title">Getting your plan...</h1>
          </div>
        )}

        {status === 'checkout' && (
          <div className="ow-plan__result" role="status">
            <span className="ow-plan__spinner" aria-hidden="true" />
            <h1 className="ow-plan__title">Taking you to secure checkout...</h1>
            <p className="ow-plan__sub">
              Your card details are handled by Stripe - we never see them.
            </p>
            {/* createCheckoutSession() swallows its own errors and redirects on
                success, so a page still sitting here means the hand-off failed.
                Say so rather than spinning forever. */}
            {checkoutStalled && (
              <>
                <p className="ow-plan__sub">
                  That is taking longer than it should. Check your connection and try again.
                </p>
                <button className="ow-plan__go" type="button" onClick={() => setStatus('ready')}>
                  Back to your plan
                </button>
              </>
            )}
          </div>
        )}

        {status === 'unavailable' && (
          <div className="ow-plan__result" role="status">
            <span className="ow-plan__badge ow-plan__badge--warn" aria-hidden="true">
              !
            </span>
            <h1 className="ow-plan__title">This link has expired.</h1>
            <p className="ow-plan__sub">
              Change-plan links are good for one hour. Open <b>Change plan</b> from the Songhunt menu
              in Overwolf again and we will pick up where you left off.
            </p>
            <a className="ow-plan__go" href="https://www.songhunt.ai/">
              Go to Songhunt.ai
            </a>
          </div>
        )}

        {status === 'ready' && (
          <>
            <div className="ow-plan__head">
              <p className="ow-plan__eyebrow">Your plan</p>
              <h1 className="ow-plan__title">You are on {currentRow.name}.</h1>
              {email && (
                <span className="ow-plan__who">
                  <span className="ow-plan__avatar" aria-hidden="true">
                    {email[0]}
                  </span>
                  Signed in as <b>{email}</b>
                </span>
              )}
            </div>

            <ul className="ow-plan__cards">
              {PLAN_ROWS.map(row => {
                const isCurrent = row.key === currentPlan;
                const isUpgrade = rank(row.key) > rank(currentPlan);

                return (
                  <li
                    className={`ow-plan__card-item${isCurrent ? ' ow-plan__card-item--current' : ''}`}
                    key={row.key}
                  >
                    <div className="ow-plan__band">
                      {isCurrent && <span className="ow-plan__ribbon">YOUR PLAN</span>}
                      <h2 className="ow-plan__plan-name">{row.name}</h2>
                      <p className="ow-plan__plan-sub">{row.sub}</p>
                      <div className="ow-plan__price">
                        <span className="ow-plan__price-n">{row.price}</span>
                        <span className="ow-plan__price-p">/mo</span>
                      </div>
                    </div>

                    <ul className="ow-plan__features">
                      {row.features.map(feature => (
                        <li className="ow-plan__feature" key={feature}>
                          <span
                            className={`ow-plan__tick${row.key === 'none' ? ' ow-plan__tick--dim' : ''}`}
                            aria-hidden="true"
                          >
                            &#10003;
                          </span>
                          {feature}
                        </li>
                      ))}
                    </ul>

                    {/* The plan they hold keeps the slot so all three cards stay
                        the same height, but it is not a control. */}
                    {isCurrent ? (
                      <div className="ow-plan__cta ow-plan__cta--inert">This is your plan</div>
                    ) : (
                      <button
                        className={`ow-plan__cta ow-plan__cta--${isUpgrade ? 'up' : 'down'}`}
                        type="button"
                        onClick={() => {
                          if (isUpgrade) {
                            applyChange(row);
                          } else {
                            // Going down warns first; going up has nothing to warn about.
                            setDialogPlan(row);
                            setDialogStep('confirm');
                          }
                        }}
                      >
                        {isUpgrade ? `Upgrade to ${row.name}` : `Switch to ${row.name}`}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>

            <p className="ow-plan__foot">
              Changes apply to this account everywhere - the app and Songhunt.ai.
            </p>
          </>
        )}
      </main>

      {/* One dialog for both steps: a downgrade names what stops working before
          anything happens, and every change that needs an e-mail ends here with
          the message ready to send. The plans stay behind the scrim - this step
          is about the e-mail, not about picking again. */}
      {dialogPlan && (
        <div
          className="ow-plan__scrim"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ow-plan-confirm"
        >
          <div className="ow-plan__dialog">
            {dialogStep === 'confirm' ? (
              <>
                <span className="ow-plan__badge ow-plan__badge--warn" aria-hidden="true">
                  !
                </span>
                <h2 className="ow-plan__dialog-title" id="ow-plan-confirm">
                  Switch to {dialogPlan.name}?
                </h2>
                <p className="ow-plan__sub">Here is what you give up:</p>

                <ul className="ow-plan__loses">
                  {currentRow.loses.map(item => (
                    <li key={item}>
                      <span className="ow-plan__x" aria-hidden="true">
                        &times;
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>

                <div className="ow-plan__dialog-actions">
                  <button className="ow-plan__keep" type="button" onClick={closeDialog}>
                    Keep {currentRow.name}
                  </button>
                  <button className="ow-plan__confirm" type="button" onClick={confirmDowngrade}>
                    Yes, switch
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 className="ow-plan__dialog-title" id="ow-plan-confirm">
                  One last step to move to {dialogPlan.name}.
                </h2>
                <p className="ow-plan__sub">
                  Plan changes are handled by a person, so we need it in writing. Send us an e-mail
                  with the subject below and we will move you and confirm
                  {email ? <> at <b>{email}</b></> : null}. Nothing changes until we receive it - you
                  keep {currentRow.name} in the meantime.
                </p>

                <AccountRequestEmailStep
                  block="ow-plan"
                  request={buildPlanChangeEmail({
                    email,
                    fromPlan: currentRow.name,
                    toPlan: dialogPlan.name,
                    // The SH1 link carries no id; this comes from the account we
                    // signed into, so it is only here when that succeeded.
                    userId: user?.id != null ? String(user.id) : undefined,
                  })}
                  action="Open my email app"
                />

                <button className="ow-plan__dismiss" type="button" onClick={closeDialog}>
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
